import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, documentId } from 'firebase/firestore';
import { db } from './firebase';
import './ManageBookings.css';

interface Booking {
  id: string;
  userId: string;
  providerId?: string;
  userInfo: {
    name: string;
    email: string;
    phone?: string;
  };
  subcategoryId: string;
  serviceItem: {
    name: string;
    price: number;
    priceUnit?: string;
    description: string;
    jobDetails: string;
    duration: string;
  };
  originalPrice?: number;
  finalPrice?: number;
  discountCode?: string | null;
  discountApplied?: {
    code: string;
    discountType: 'percentage' | 'fixed_amount';
    discountValue: number;
    discountAmount: number;
  } | null;
  dateTime: string;
  address: string;
  note?: string | null;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'pending_completion';
  providerQueue?: string[];
  completionReportedAt?: any;
  createdAt: any;
  hasReview?: boolean;
}

interface BookingWithProvider extends Booking {
  providerName: string;
  subcategoryName: string;
}

const ManageBookings: React.FC = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingWithProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [packageFilter, setPackageFilter] = useState<string>('');
  const [serviceFilter, setServiceFilter] = useState<string>('');
  const [timeFilter, setTimeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;

  const statusOptions = [
    { value: 'all', label: 'Tất cả' },
    { value: 'pending', label: 'Chờ xử lý' },
    { value: 'confirmed', label: 'Đã xác nhận' },
    { value: 'cancelled', label: 'Đã hủy' },
    { value: 'completed', label: 'Hoàn thành' },
    { value: 'pending_completion', label: 'Chờ hoàn thành' }
  ];

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Xây dựng điều kiện query server-side cho status/time để giảm dữ liệu tải về
      const constraints: any[] = [];

      if (statusFilter !== 'all') {
        constraints.push(where('status', '==', statusFilter));
      }

      if (timeFilter !== 'all') {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let fromDate = startOfDay;
        if (timeFilter === 'today') {
          fromDate = startOfDay;
        } else if (timeFilter === 'last7days') {
          const d = new Date(startOfDay);
          d.setDate(d.getDate() - 7);
          fromDate = d;
        } else if (timeFilter === 'last30days') {
          const d = new Date(startOfDay);
          d.setDate(d.getDate() - 30);
          fromDate = d;
        } else if (timeFilter === 'last90days') {
          const d = new Date(startOfDay);
          d.setDate(d.getDate() - 90);
          fromDate = d;
        }
        constraints.push(where('createdAt', '>=', fromDate));
      }

      // Luôn orderBy để lấy mới nhất trước
      let bookingsQuery;
      try {
        bookingsQuery = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'), ...constraints);
      } catch (e) {
        // Fallback tránh lỗi construct query
        bookingsQuery = collection(db, 'bookings');
      }

      let querySnapshot;
      try {
        querySnapshot = await getDocs(bookingsQuery as any);
      } catch (e) {
        // Fallback nếu thiếu index hoặc lỗi khác
        console.warn('Query with constraints failed, falling back to full scan:', e);
        querySnapshot = await getDocs(collection(db, 'bookings'));
      }

      const rawBookings: Booking[] = querySnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) })) as Booking[];

      // Thu thập ID cần tra cứu
      const providerIds = new Set<string>();
      const subcategoryIds = new Set<string>();
      for (const b of rawBookings) {
        if (b.providerId) providerIds.add(b.providerId);
        if (b.subcategoryId) subcategoryIds.add(b.subcategoryId);
      }

      // Helper chunk và fetch theo lô với where(documentId(), 'in', [...]) (tối đa 10 mỗi lô)
      const chunk = <T,>(arr: T[], size: number) => {
        const out: T[][] = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      };

      const fetchNamesByIds = async (col: string, ids: Set<string>) => {
        const idList = Array.from(ids).filter(Boolean);
        const batches = chunk(idList, 10);
        const map: Record<string, string> = {};
        for (const batch of batches) {
          const snap = await getDocs(query(collection(db, col), where(documentId(), 'in', batch)));
          snap.forEach((d) => {
            const data = d.data() as any;
            map[d.id] = (data && data.name) || 'Không có tên';
          });
        }
        return map;
      };

      // Chạy song song để giảm thời gian chờ
      const [providerMap, subcategoryMap] = await Promise.all([
        fetchNamesByIds('users', providerIds),
        fetchNamesByIds('subcategories', subcategoryIds)
      ]);

      // Gắn tên vào booking
      const bookingsData: BookingWithProvider[] = rawBookings.map((b) => ({
        ...b,
        providerName: b.providerId ? (providerMap[b.providerId] || 'Không có tên') : 'Chưa có',
        subcategoryName: b.subcategoryId ? (subcategoryMap[b.subcategoryId] || 'Không có tên') : 'Không xác định'
      }));

      setBookings(bookingsData);
    } catch (error) {
      console.error('Lỗi khi tải danh sách booking:', error);
      setError('Không thể tải danh sách booking');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('vi-VN');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatPrice = (price: number | undefined) => {
    if (price === undefined || price === null) return 'N/A';
    return price.toLocaleString('vi-VN') + ' VND';
  };

  const applyFilters = (bookingsToFilter: BookingWithProvider[]) => {
    let filtered = bookingsToFilter;
    
    // Filter theo trạng thái
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }
    
    // Filter theo nhà cung cấp
    if (providerFilter !== 'all') {
      filtered = filtered.filter(booking => booking.providerName === providerFilter);
    }
    
    // Filter theo gói (serviceItem.name)
    if (packageFilter.trim()) {
      filtered = filtered.filter(booking => 
        booking.serviceItem.name.toLowerCase().includes(packageFilter.trim().toLowerCase())
      );
    }
    
    // Filter theo dịch vụ (subcategoryName)
    if (serviceFilter.trim()) {
      filtered = filtered.filter(booking => 
        booking.subcategoryName.toLowerCase().includes(serviceFilter.trim().toLowerCase())
      );
    }
    
    // Filter theo thời gian
    if (timeFilter !== 'all') {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(booking => {
        if (!booking.createdAt) return false;
        
        const bookingDate = booking.createdAt.toDate ? booking.createdAt.toDate() : new Date(booking.createdAt);
        
        switch (timeFilter) {
          case 'today':
            return bookingDate >= startOfDay;
          case 'last7days':
            const last7Days = new Date(startOfDay);
            last7Days.setDate(last7Days.getDate() - 7);
            return bookingDate >= last7Days;
          case 'last30days':
            const last30Days = new Date(startOfDay);
            last30Days.setDate(last30Days.getDate() - 30);
            return bookingDate >= last30Days;
          case 'last90days':
            const last90Days = new Date(startOfDay);
            last90Days.setDate(last90Days.getDate() - 90);
            return bookingDate >= last90Days;
          default:
            return true;
        }
      });
    }
    
    return filtered;
  };

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'pending': 'Chờ xử lý',
      'confirmed': 'Đã xác nhận',
      'cancelled': 'Đã hủy',
      'completed': 'Hoàn thành',
      'pending_completion': 'Chờ hoàn thành'
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status: string) => {
    const statusClassMap: { [key: string]: string } = {
      'pending': 'status-pending',
      'confirmed': 'status-confirmed',
      'cancelled': 'status-cancelled',
      'completed': 'status-completed',
      'pending_completion': 'status-pending-completion'
    };
    return statusClassMap[status] || '';
  };

  const getRowClass = (status: string) => {
    const rowClassMap: { [key: string]: string } = {
      'pending': 'row-pending',
      'confirmed': 'row-confirmed',
      'cancelled': 'row-cancelled',
      'completed': 'row-completed',
      'pending_completion': 'row-pending-completion'
    };
    return rowClassMap[status] || '';
  };

  // Derived: provider options, filtered list và trang hiện tại
  const providerOptions = useMemo(() => {
    return Array.from(new Set(bookings.map(b => b.providerName))).sort();
  }, [bookings]);

  const filteredBookings = useMemo(() => applyFilters(bookings), [bookings, statusFilter, providerFilter, packageFilter, serviceFilter, timeFilter]);

  const totalPages = useMemo(() => Math.ceil(filteredBookings.length / recordsPerPage), [filteredBookings.length, recordsPerPage]);

  const displayedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    return filteredBookings.slice(startIndex, startIndex + recordsPerPage);
  }, [filteredBookings, currentPage, recordsPerPage]);

  // Reset trang nếu vượt quá tổng trang
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Tải dữ liệu khi vào trang và khi status/time filter đổi để tận dụng server-side filtering
  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, timeFilter]);

  if (loading) {
    return (
      <div className="manage-bookings">
        <div className="page-header">
          <h2>Quản lý Booking</h2>
          <p className="page-subtitle">Quản lý và theo dõi tất cả booking trong hệ thống</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-bookings">
      <div className="page-header">
        <h2>Quản lý Booking</h2>
        <p className="page-subtitle">Quản lý và theo dõi tất cả booking trong hệ thống</p>
      </div>
      
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="status-filter">Trạng thái:</label>
          <select 
            id="status-filter"
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="provider-filter">Nhà cung cấp:</label>
          <select 
            id="provider-filter"
            value={providerFilter} 
            onChange={(e) => setProviderFilter(e.target.value)}
          >
            <option value="all">Tất cả</option>
            {providerOptions.map(provider => (
              <option key={provider} value={provider}>
                {provider}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="package-filter">Gói:</label>
          <input
            id="package-filter"
            type="text"
            placeholder="Tìm theo tên gói..."
            value={packageFilter}
            onChange={(e) => setPackageFilter(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label htmlFor="service-filter">Dịch vụ:</label>
          <input
            id="service-filter"
            type="text"
            placeholder="Tìm theo dịch vụ..."
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label htmlFor="time-filter">Thời gian:</label>
          <select 
            id="time-filter"
            value={timeFilter} 
            onChange={(e) => setTimeFilter(e.target.value)}
          >
            <option value="all">Tất cả</option>
            <option value="today">Hôm nay</option>
            <option value="last7days">7 ngày qua</option>
            <option value="last30days">30 ngày qua</option>
            <option value="last90days">90 ngày qua</option>
          </select>
        </div>
        
        <button onClick={fetchBookings} className="refresh-button">
          Làm mới
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <table className="bookings-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Booking ID</th>
              <th>Khách hàng</th>
              <th>Nhà cung cấp</th>
              <th>Gói</th>
              <th>Dịch vụ</th>
              <th>Giá cuối</th>
              <th>Trạng thái</th>
              <th>Thời gian</th>
            </tr>
          </thead>
          <tbody>
            {displayedBookings.length === 0 ? (
              <tr>
                <td colSpan={9} className="no-data">
                  {filteredBookings.length === 0 ? 'Không có booking nào' : 'Không có dữ liệu cho trang này'}
                </td>
              </tr>
            ) : (
              displayedBookings.map((booking, index) => (
                <tr key={booking.id} className={getRowClass(booking.status)}>
                  <td className="order-cell">
                    <span className="order-display">{(currentPage - 1) * recordsPerPage + index + 1}</span>
                  </td>
                  <td>
                    <button 
                      className="booking-id-link"
                      onClick={() => navigate(`/admin/bookings/${booking.id}`)}
                      title="Xem chi tiết booking"
                    >
                      {booking.id}
                    </button>
                  </td>
                  <td>
                    {booking.userInfo.name}
                  </td>
                  <td>{booking.providerName}</td>
                  <td>{booking.serviceItem.name}</td>
                  <td>{booking.subcategoryName}</td>
                  <td className="price-cell">{formatPrice(booking.finalPrice)}</td>
                  <td>
                    <span className={`status ${getStatusClass(booking.status)}`}>
                      {getStatusLabel(booking.status)}
                    </span>
                  </td>
                  <td>{formatDateTime(booking.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Hiển thị {(currentPage - 1) * recordsPerPage + 1} - {Math.min(currentPage * recordsPerPage, filteredBookings.length)} của {filteredBookings.length} kết quả
          </div>
          <div className="pagination-controls">
            <button 
              onClick={() => setCurrentPage(1)} 
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              ««
            </button>
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              «
            </button>
            
            {/* Page numbers */}
            {(() => {
              const pages = [];
              const startPage = Math.max(1, currentPage - 2);
              const endPage = Math.min(totalPages, currentPage + 2);
              
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button 
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    disabled={currentPage === i}
                    className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
                  >
                    {i}
                  </button>
                );
              }
              return pages;
            })()}
            
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              »
            </button>
            <button 
              onClick={() => setCurrentPage(totalPages)} 
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              »»
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBookings; 